import { parseRequestError } from '@/app/common/utilities'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { PURSE_KEYS } from '../../../../../queries/program_purses/keys.ts'
import { CreatePurseDialog, CreatePurseFormValues } from './create-purse-dialog'
import { FundingSource } from '../../schema'

// Create Program Purse
export type PostProgramPursePayload = {
  name: string
  source_type: string
  total: string
  external_provider_id: string
  sub_source_type: string
}

export async function POSTProgramPurse(
  programId: number,
  payload: PostProgramPursePayload
) {
  const response = await axios.post(`/programs/${programId}/purses`, payload)
  return response.data
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  fundingSources: FundingSource[]
}

export default function CreatePurse(props: Props) {
  const { onOpenChange, open, fundingSources } = props
  const programId = useSelector(selectSelectedProgram).id
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const mutation = useMutation({
    mutationFn: (payload: PostProgramPursePayload) =>
      POSTProgramPurse(programId, payload),
  })

  function handleFormSubmit(payload: CreatePurseFormValues) {
    const payloadRefined: PostProgramPursePayload = {
      sub_source_type: payload.sub_source_type,
      name: payload.name,
      source_type: payload.source_type,
      total: payload.total.toString(),
      external_provider_id: payload.achMid,
    }

    mutation.mutate(payloadRefined, {
      onSuccess: () => {
        toast({
          variant: 'default',
          title: 'Purse Created',
          description: 'Your purse has been created successfully.',
        })
        onOpenChange(false)
        queryClient.invalidateQueries({
          queryKey: PURSE_KEYS.PURSES(programId),
        })
      },
      onError: (error) => {
        const parsedError = parseRequestError(error)
        toast({
          variant: 'destructive',
          title: 'Error Creating Purse',
          description: parsedError.message,
        })
        toast({
          variant: 'destructive',
          description: parsedError.message,
        })
      },
    })
  }

  return (
    <CreatePurseDialog
      mutationStatus={mutation.status}
      fundingSources={fundingSources}
      open={open}
      onOpenChange={onOpenChange}
      onCreatePurse={handleFormSubmit}
    />
  )
}
